import type {
  AppNotesInstance,
  ConnectedNote,
  createAppNotes,
} from "@plannotator/appnotes";

/**
 * The one hosted dashboard that reviews itself with AppNotes. Exact origin
 * only: production, local dev, self-hosted and customer builds never load
 * the SDK.
 */
export const stagingDashboardOrigin = "https://staging.appnotes.dev";

/** The "AppNotes Staging" Site on the Plannotator team in AppNotes staging. */
export const stagingDashboardSiteId = "ansite_64bb44cb738545da847a64900f04180a";

const launchParameter = "appnotes_token";

/** The parts of `window` the self-review reads and writes. */
export interface SelfReviewHost {
  readonly location: {
    readonly origin: string;
    readonly pathname: string;
    readonly search: string;
    readonly hash: string;
  };
  readonly history: {
    readonly state: unknown;
    replaceState(data: unknown, unused: string, url: string): void;
  };
}

export type SdkLoader = () => Promise<{
  readonly createAppNotes: typeof createAppNotes;
}>;

/** `off`: not the staging origin. `not_mounted`: dormant or failed. */
export type SelfReviewOutcome = "off" | "mounted" | "not_mounted";

export function selfReviewEnabled(origin: string): boolean {
  return origin === stagingDashboardOrigin;
}

export function hasLaunchFragment(hash: string): boolean {
  return new URLSearchParams(hash.replace(/^#/u, "")).has(launchParameter);
}

/** Drop only the launch credential; any other fragment value stays. */
export function removeLaunchFragment(host: SelfReviewHost): void {
  const parameters = new URLSearchParams(host.location.hash.replace(/^#/u, ""));
  if (!parameters.has(launchParameter)) return;
  parameters.delete(launchParameter);
  const rest = parameters.toString();
  host.history.replaceState(
    host.history.state,
    "",
    `${host.location.pathname}${host.location.search}${rest.length === 0 ? "" : `#${rest}`}`,
  );
}

/**
 * Loads and mounts the connected SDK on the staging dashboard. The SDK reads
 * and strips the launch fragment itself; this only removes it when the SDK
 * did not mount. Starts run one after another, so a fragment-only Review
 * launch (a `hashchange`, no reload) replaces the mounted instance.
 */
export function createSelfReview(
  host: SelfReviewHost,
  load: SdkLoader = () => import("@plannotator/appnotes"),
): { readonly start: () => Promise<SelfReviewOutcome> } {
  let current: AppNotesInstance<ConnectedNote> | null = null;
  let queue: Promise<unknown> = Promise.resolve();

  async function run(): Promise<SelfReviewOutcome> {
    if (!selfReviewEnabled(host.location.origin)) {
      removeLaunchFragment(host);
      return "off";
    }
    try {
      const { createAppNotes } = await load();
      const previous = current;
      current = null;
      await previous?.destroy();
      const appNotes = createAppNotes({
        mode: "connected",
        siteId: stagingDashboardSiteId,
        apiOrigin: stagingDashboardOrigin,
      });
      const mounted = await appNotes.mount();
      if (mounted.ok) {
        current = appNotes;
        return "mounted";
      }
    } catch {
      // A failed chunk load or mount leaves the dashboard as it was.
    }
    removeLaunchFragment(host);
    return "not_mounted";
  }

  return {
    start() {
      const next = queue.then(run, run);
      queue = next;
      return next;
    },
  };
}
